import { Checkbox, Divider, FormControlLabel, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import DialogContentText from '@mui/material/DialogContentText';
import { DatePicker } from '@mui/x-date-pickers';
import { format, parse, startOfMonth } from 'date-fns';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import UserAutoComplete from '@sophocles/components/UserAutoComplete';
import { fetchAllUserSellers } from '@sophocles/entities/User';
import { createBillingAccountAgreement, editBillingAccountAgreement } from '@sophocles/features/billing/api';
import { selectBillingAccountAgreement } from '@sophocles/features/billing/slice';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import BillingFrequencySelect from '@sophocles/views/billing/components/BillingFrequencySelect';
import InventoryPolicySelect from '@sophocles/views/billing/components/InventoryPolicySelect';

const KNOWN_FIELDS = [
  'account',
  'primary_sales_person',
  'is_signed',
  'is_active',
  'billing_frequency',
  'annual_start_date',
  'is_billed_by_site_group',
  'is_billed_virtual',
  'inventory_policy',
  'inventory_amount',
  'device_price',
  'inventory_price',
  'virtual_price',
  'start_date',
  'end_date',
  'pricing_guarantee_start_date',
  'pricing_guarantee_end_date',
];

function AgreementDialog({ accountId, id, open, onSubmit, onClose, onDelete, ...props }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const agreement = useSelector(selectBillingAccountAgreement(id));

  const [users, setUsers] = useState([]);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = (values, { setErrors }) => {
    setNonFieldMessages([]);
    if (id != null) {
      if (values.inventory_policy == 'none' || values.inventory_policy == 'exact') {
        values.inventory_amount = 0;
      }
      if (values.annual_start_date) {
        values.annual_start_date = format(values.annual_start_date, 'yyyy-MM-01');
      }
      return dispatch(editBillingAccountAgreement({ id, ...values }))
        .unwrap()
        .then(() => {
          if (onSubmit) {
            onSubmit(values);
          }
          handleClose();
        })
        .catch(error => {
          setNonFieldMessages(
            applyApiErrorToFormik(error, {
              setErrors,
              knownFields: KNOWN_FIELDS,
              genericMessage: t('billing.account.agreementSaveFailed'),
            }),
          );
        });
    }
    return dispatch(createBillingAccountAgreement(values))
      .unwrap()
      .then(() => {
        if (onSubmit) {
          onSubmit(values);
        }
        handleClose();
      })
      .catch(error => {
        setNonFieldMessages(applyApiErrorToFormik(error, { setErrors, knownFields: KNOWN_FIELDS }));
      });
  };

  useEffect(() => {
    dispatch(fetchAllUserSellers()).then(res => setUsers(res));
  }, []);

  useEffect(() => {
    if (open) {
      setNonFieldMessages([]);
    }
  }, [open]);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        account: accountId,
        primary_sales_person: agreement?.primary_sales_person?.id,
        is_signed: agreement?.is_signed ?? false,
        is_active: agreement?.is_active ?? false,
        billing_frequency: agreement?.billing_frequency ?? 'month',
        annual_start_date:
          agreement?.annual_start_date != null ? parse(agreement?.annual_start_date, 'yyyy-MM-01', new Date()) : null,
        is_billed_by_site_group: agreement?.is_billed_by_site_group ?? false,
        is_billed_virtual: agreement?.is_billed_virtual ?? false,
        // is_billed_prorated: agreement?.is_billed_prorated ?? false,
        inventory_policy: agreement?.inventory_policy ?? 'exact',
        inventory_amount: agreement?.inventory_amount,
        device_price: agreement?.device_price ?? 0,
        inventory_price: agreement?.inventory_price ?? 0,
        virtual_price: agreement?.virtual_price ?? 0,
        start_date: agreement?.start_date,
        end_date: agreement?.end_date,
        pricing_guarantee_start_date: agreement?.pricing_guarantee_start_date,
        pricing_guarantee_end_date: agreement?.pricing_guarantee_end_date,
      }}
      validationSchema={yup.object().shape({
        is_active: yup.boolean(),
        is_signed: yup.boolean(),
        is_billed_by_site_group: yup.boolean(),
        is_billed_virtual: yup.boolean(),
        // is_billed_prorated: yup.boolean(),
        start_date: yup.date().required(),
        end_date: yup.date().required(),
        annual_start_date: yup.date().nullable(),
        billing_frequency: yup.string().required(),
        inventory_policy: yup.string().required(),
        primary_sales_person: yup.number().nullable(),
        device_price: yup.number().nullable().min(0),
        inventory_price: yup.number().nullable().min(0),
        virtual_price: yup.number().nullable().min(0),
        pricing_guarantee_start_date: yup.date().nullable(),
        pricing_guarantee_end_date: yup.date().nullable(),
        inventory_amount: yup.number().nullable().min(0).max(100),
      })}
      onSubmit={handleSubmit}
    >
      {({ values, touched, errors, setFieldValue, submitForm, resetForm, handleChange, handleBlur }) => (
        <Form>
          <ResponsiveDialog
            title={
              agreement != null
                ? t('billing.editAccountAgreement', 'Edit Account Agreement')
                : t('billing.createAccountAgreement', 'Create Account Agreement')
            }
            open={open}
            canSubmit={true}
            onClose={() => {
              resetForm();
              handleClose();
            }}
            onSubmit={() => {
              submitForm();
            }}
            {...props}
          >
            <DialogContentText sx={{ my: 2 }}>
              {id != null
                ? t(
                    'billing.agreementDialogInstructionEdit',
                    'To edit this agreement, please enter all required details below.',
                  )
                : t(
                    'billing.agreementDialogInstructionCreate',
                    'To create this agreement, please enter all required details below.',
                  )}
            </DialogContentText>
            <FormErrorAlert messages={nonFieldMessages} sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <FormControlLabel
                sx={{ margin: 0, alignItems: 'center' }}
                control={<Checkbox checked={values.is_active} onChange={handleChange} name="is_active" />}
                label={t('billing.active', 'Active')}
              />
              <FormControlLabel
                sx={{ margin: 0, alignItems: 'center' }}
                control={<Checkbox checked={values.is_signed} onChange={handleChange} name="is_signed" />}
                label={t('billing.signed', 'Signed')}
              />
              <UserAutoComplete
                fullWidth
                label={t('billing.salesperson', 'Salesperson')}
                value={values.primary_sales_person}
                users={users}
                sx={{ flex: 1 }}
                onChange={e => {
                  if (e.target.value === '') {
                    e.target.value = null;
                  }
                  setFieldValue('primary_sales_person', e.target.value);
                  handleChange(e);
                }}
              />
              <Field
                as={TextField}
                name="start_date"
                type="date"
                label={t('billing.startDate', 'Start Date')}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                error={touched.start_date && Boolean(errors.start_date)}
                helperText={touched.start_date && errors.start_date}
              />
              <Field
                as={TextField}
                name="end_date"
                type="date"
                label={t('billing.endDate', 'End Date')}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                error={touched.end_date && Boolean(errors.end_date)}
                helperText={touched.end_date && errors.end_date}
              />
              <FormControlLabel
                sx={{ margin: 0, alignItems: 'center' }}
                control={
                  <Checkbox
                    checked={values.is_billed_by_site_group}
                    onChange={handleChange}
                    name="is_billed_by_site_group"
                  />
                }
                label={t('billing.billSiteGroupsSeparately', 'Bill Site Groups Separately')}
              />
              <FormControlLabel
                sx={{ margin: 0, alignItems: 'center' }}
                control={
                  <Checkbox checked={values.is_billed_virtual} onChange={handleChange} name="is_billed_virtual" />
                }
                label={t('billing.billVirtualAccess', 'Bill Virtual Access')}
              />
              {/* <FormControlLabel
                sx={{ margin: 0, alignItems: 'center' }}
                control={
                  <Checkbox checked={values.is_billed_prorated} onChange={handleChange} name="is_billed_prorated" />
                }
                label="Bill Prorated for Installs"
              /> */}
              <Divider />
              <Typography variant="h6">{t('billing.pricing', 'Pricing')}</Typography>
              <BillingFrequencySelect
                size="small"
                value={values.billing_frequency}
                onChange={newFrequency => setFieldValue('billing_frequency', newFrequency)}
              />
              {values.billing_frequency === 'annual' && (
                <Field name="annual_start_date">
                  {({ field, form }) => (
                    <DatePicker
                      views={['month']} // Restricts selection to year & month
                      label={t('billing.annualStartMonth', 'Annual Start Month')}
                      value={field.value}
                      onChange={date => {
                        if (date) {
                          form.setFieldValue(field.name, startOfMonth(date)); // Store as yyyy-MM-01
                        } else {
                          form.setFieldValue(field.name, ''); // Handle clearing value
                        }
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: form.touched.annual_start_date && Boolean(form.errors.annual_start_date),
                          helperText: form.touched.annual_start_date && form.errors.annual_start_date,
                        },
                      }}
                    />
                  )}
                </Field>
              )}
              <Field
                as={TextField}
                name="pricing_guarantee_start_date"
                type="date"
                label={t('billing.pricingGuaranteeStartDate', 'Pricing Guarantee Start Date')}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                error={touched.pricing_guarantee_start_date && Boolean(errors.pricing_guarantee_start_date)}
                helperText={touched.pricing_guarantee_start_date && errors.pricing_guarantee_start_date}
              />
              <Field
                as={TextField}
                name="pricing_guarantee_end_date"
                type="date"
                label={t('billing.pricingGuaranteeEndDate', 'Pricing Guarantee End Date')}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                error={touched.pricing_guarantee_end_date && Boolean(errors.pricing_guarantee_end_date)}
                helperText={touched.pricing_guarantee_end_date && errors.pricing_guarantee_end_date}
              />
              <Field
                as={TextField}
                name="device_price"
                type="number"
                label={t('billing.dataAccessFee', 'Data Access Fee')}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                error={touched.device_price && Boolean(errors.device_price)}
                helperText={touched.device_price && errors.device_price}
              />
              {values.is_billed_virtual && (
                <Field
                  as={TextField}
                  name="virtual_price"
                  type="number"
                  label={t('billing.virtualAccessFee', 'Virtual Access Fee')}
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  error={touched.virtual_price && Boolean(errors.virtual_price)}
                  helperText={touched.virtual_price && errors.virtual_price}
                />
              )}
              <Divider />
              <Typography variant="h6">{t('billing.inventory', 'Inventory')}</Typography>
              <InventoryPolicySelect
                value={values.inventory_policy}
                error={touched.inventory_policy && Boolean(errors.inventory_policy)}
                helperText={touched.inventory_policy && errors.inventory_policy}
                onChange={v => {
                  setFieldValue('inventory_policy', v);
                }}
              />
              {values.inventory_policy != 'none' && values.inventory_policy != 'exact' && (
                <Field name="inventory_amount">
                  {({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      label={
                        values.inventory_policy === 'percentage'
                          ? t('billing.percentage', 'Percentage')
                          : t('billing.fixedAmount', 'Fixed Amount')
                      }
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {values.inventory_policy === 'percentage' ? '%' : '#'}
                          </InputAdornment>
                        ),
                      }}
                      error={touched.inventory_amount && Boolean(errors.inventory_amount)}
                      helperText={touched.inventory_amount && errors.inventory_amount}
                    />
                  )}
                </Field>
              )}
              {values.inventory_policy != 'none' && (
                <Field
                  as={TextField}
                  name="inventory_price"
                  type="number"
                  label={t('billing.inventoryFee', 'Inventory Fee')}
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  error={touched.inventory_price && Boolean(errors.inventory_price)}
                  helperText={touched.inventory_price && errors.inventory_price}
                />
              )}
            </Stack>
          </ResponsiveDialog>
        </Form>
      )}
    </Formik>
  );
}

export default AgreementDialog;
